import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { Typography } from "../../../Components/Typography";
import Card from "../../../Components/Card";
import Form from "../../../Components/Form";
import Button from "../../../Components/Button";
import { updateUser, getAdminDropdownOptions } from "../../../Utils/apiServices";
import { useAuth } from "../../../Context/authContext";

const MAX_EXPERIENCE_SELECT = 3;
const parseMultiSelectStr = (s) =>
  Array.isArray(s) ? s : String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
const toCommaSeparated = (val) =>
  Array.isArray(val) ? val.join(",") : (val != null && val !== "" ? String(val) : "");

const SkillsTab = ({ initialFormData, onUpdate }) => {
  const { user, updateUser: updateAuthUser } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });
  const [formData, setFormData] = useState({});
  const formRef = useRef(null);
  const [experienceOptions, setExperienceOptions] = useState({
    industry_experience: [],
    functional_experience: [],
    technology_stack: [],
  });

  useEffect(() => {
    if (initialFormData) {
      const profileData = {
        years_of_experience: initialFormData.years_of_experience || 
                           initialFormData.experience_years || 
                           user?.years_of_experience || 
                           user?.experience_years || 
                           "",
        industry_experience: parseMultiSelectStr(initialFormData.industry_experience || initialFormData.industry || user?.industry || ""),
        functional_experience: parseMultiSelectStr(initialFormData.functional_experience || user?.functional_experience || ""),
        technology_stack: parseMultiSelectStr(initialFormData.technology_stack || user?.technology_stack || ""),
      };
      setFormData(profileData);
    }
  }, [initialFormData, user]);

  // Fetch dropdown options for experience fields (same API as Complete Registration)
  useEffect(() => {
    const parseOptions = (res) => {
      if (!res?.success || res?.data === undefined) return [];
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload) ? payload : (payload?.options ?? []);
      return (Array.isArray(list) ? list : [])
        .filter((o) => o && (o?.is_active !== false && o?.enabled !== false))
        .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
        .map((o) => ({ value: String(o?.value ?? o?.label ?? ""), label: String(o?.value ?? o?.label ?? "") }))
        .filter((o) => o.value);
    };
    const fetchOptions = async () => {
      try {
        const [industryRes, functionalRes, techRes] = await Promise.all([
          getAdminDropdownOptions("industry_experience", true),
          getAdminDropdownOptions("functional_experience", true),
          getAdminDropdownOptions("technology_stack", true),
        ]);
        setExperienceOptions({
          industry_experience: parseOptions(industryRes),
          functional_experience: parseOptions(functionalRes),
          technology_stack: parseOptions(techRes),
        });
      } catch (err) {
        console.error("Error loading experience dropdown options:", err);
      }
    };
    fetchOptions();
  }, []);

  const getFormFields = () => {
    return [
      {
        name: "section_skills",
        type: "custom",
        component: (
          <></>
          // <div className="flex flex-col gap-1 py-4 border-b border-slate-200">
          //   <Typography as="h2" className="text-lg font-bold text-primary-900">
          //     Skills
          //   </Typography>
          //   <Typography as="p" className="text-sm text-tertiary-600">
          //     Update your skills here.
          //   </Typography>
          // </div>
        ),
      },
      {
        name: "years_of_experience",
        label: "Years of Experience",
        type: "number",
        required: true,
        placeholder: "Enter your years of experience",
      },
      {
        name: "industry_experience",
        label: "Industry Experience",
        type: "custom",
        required: true,
        validate: (val) => (!Array.isArray(val) || val.length === 0) ? "At least one selection is required" : (val.length > MAX_EXPERIENCE_SELECT ? `Maximum ${MAX_EXPERIENCE_SELECT} selections` : undefined),
        render: (value, formData, handleInputChange, error) => {
          const arr = Array.isArray(formData.industry_experience) ? formData.industry_experience : parseMultiSelectStr(formData.industry_experience);
          return (
            <div className="flex flex-col gap-1">
              <div className="relative">
                <select
                  multiple
                  name="industry_experience"
                  value={arr}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    handleInputChange("industry_experience", selected.slice(0, MAX_EXPERIENCE_SELECT));
                  }}
                  className={`w-full rounded-lg border ${error ? "border-red-400" : "border-slate-200"} bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]`}
                >
                  {experienceOptions.industry_experience.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500">Hold Ctrl/Cmd to select up to {MAX_EXPERIENCE_SELECT} ({arr.length} selected)</p>
              {arr.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {arr.map((item) => (
                    <span key={item} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium">
                      {item}
                      <button type="button" onClick={() => handleInputChange("industry_experience", arr.filter((x) => x !== item))} className="hover:text-blue-900" aria-label="Remove">
                        <Icon icon="lucide:x" className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
      {
        name: "functional_experience",
        label: "Functional Experience",
        type: "custom",
        required: true,
        validate: (val) => (!Array.isArray(val) || val.length === 0) ? "At least one selection is required" : (val.length > MAX_EXPERIENCE_SELECT ? `Maximum ${MAX_EXPERIENCE_SELECT} selections` : undefined),
        render: (value, formData, handleInputChange, error) => {
          const arr = Array.isArray(formData.functional_experience) ? formData.functional_experience : parseMultiSelectStr(formData.functional_experience);
          return (
            <div className="flex flex-col gap-1">
              <div className="relative">
                <select
                  multiple
                  name="functional_experience"
                  value={arr}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    handleInputChange("functional_experience", selected.slice(0, MAX_EXPERIENCE_SELECT));
                  }}
                  className={`w-full rounded-lg border ${error ? "border-red-400" : "border-slate-200"} bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]`}
                >
                  {experienceOptions.functional_experience.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500">Hold Ctrl/Cmd to select up to {MAX_EXPERIENCE_SELECT} ({arr.length} selected)</p>
              {arr.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {arr.map((item) => (
                    <span key={item} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-100 text-green-700 text-xs font-medium">
                      {item}
                      <button type="button" onClick={() => handleInputChange("functional_experience", arr.filter((x) => x !== item))} className="hover:text-green-900" aria-label="Remove">
                        <Icon icon="lucide:x" className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
      {
        name: "technology_stack",
        label: "Technology Stack",
        type: "custom",
        required: true,
        validate: (val) => (!Array.isArray(val) || val.length === 0) ? "At least one selection is required" : (val.length > MAX_EXPERIENCE_SELECT ? `Maximum ${MAX_EXPERIENCE_SELECT} selections` : undefined),
        render: (value, formData, handleInputChange, error) => {
          const arr = Array.isArray(formData.technology_stack) ? formData.technology_stack : parseMultiSelectStr(formData.technology_stack);
          return (
            <div className="flex flex-col gap-1">
              <div className="relative">
                <select
                  multiple
                  name="technology_stack"
                  value={arr}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                    handleInputChange("technology_stack", selected.slice(0, MAX_EXPERIENCE_SELECT));
                  }}
                  className={`w-full rounded-lg border ${error ? "border-red-400" : "border-slate-200"} bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]`}
                >
                  {experienceOptions.technology_stack.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <Icon icon="lucide:chevron-down" className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500">Hold Ctrl/Cmd to select up to {MAX_EXPERIENCE_SELECT} ({arr.length} selected)</p>
              {arr.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {arr.map((item) => (
                    <span key={item} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-medium">
                      {item}
                      <button type="button" onClick={() => handleInputChange("technology_stack", arr.filter((x) => x !== item))} className="hover:text-purple-900" aria-label="Remove">
                        <Icon icon="lucide:x" className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
    ];
  };

  const getCurrentFormData = () => {
    // Use formData state which is synced in real-time via onChange callback
    const formElement = formRef.current?.querySelector('form');
    const currentData = { ...formData };
    
    if (formElement) {
      const multiSelectFields = ["industry_experience", "functional_experience", "technology_stack"];
      const inputs = formElement.querySelectorAll('input, select, textarea');
      inputs.forEach((input) => {
        if (input.name && input.type !== 'file') {
          if (multiSelectFields.includes(input.name)) return; // keep formData array
          if (input.type === 'checkbox') {
            currentData[input.name] = input.checked;
          } else {
            const domValue = input.value;
            if (domValue !== undefined && domValue !== null) {
              currentData[input.name] = domValue;
            }
          }
        }
      });
    }

    return currentData;
  };

  const handleSave = async () => {
    if (!user?.id) {
      setSaveMessage({ type: "error", text: "User ID not found. Please log in again." });
      return;
    }

    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });

    try {
      const currentData = getCurrentFormData();
      const apiData = {};

      // Only include fields with actual values
      if (currentData.years_of_experience) {
        const yearsExp = parseInt(currentData.years_of_experience);
        if (!isNaN(yearsExp)) {
          apiData.years_of_experience = yearsExp;
        }
      }

      if (currentData.industry_experience && (Array.isArray(currentData.industry_experience) ? currentData.industry_experience.length : currentData.industry_experience)) {
        apiData.industry = toCommaSeparated(currentData.industry_experience);
      }

      if (currentData.functional_experience && (Array.isArray(currentData.functional_experience) ? currentData.functional_experience.length : currentData.functional_experience)) {
        apiData.functional_experience = toCommaSeparated(currentData.functional_experience);
      }

      if (currentData.technology_stack && (Array.isArray(currentData.technology_stack) ? currentData.technology_stack.length : currentData.technology_stack)) {
        apiData.technology_stack = toCommaSeparated(currentData.technology_stack);
      }

      const response = await updateUser(user.id, apiData);

      if (response.success) {
        // Get updated user data from response
        let responseData = response.data;
        if (responseData?.data?.data) {
          responseData = responseData.data;
        }
        const updatedUserFromAPI = responseData?.data?.user || responseData?.user || null;

        const updatedUserData = {
          ...user,
          ...apiData,
          ...(updatedUserFromAPI ? updatedUserFromAPI : {}),
        };

        updateAuthUser(updatedUserData);

        // Update form data (keep multi-select fields as arrays)
        const updatedFormData = {
          ...initialFormData,
          years_of_experience: apiData.years_of_experience ?? currentData.years_of_experience ?? "",
          industry_experience: parseMultiSelectStr(apiData.industry || toCommaSeparated(currentData.industry_experience) || ""),
          functional_experience: parseMultiSelectStr(apiData.functional_experience || toCommaSeparated(currentData.functional_experience) || ""),
          technology_stack: parseMultiSelectStr(apiData.technology_stack || toCommaSeparated(currentData.technology_stack) || ""),
        };

        setFormData(updatedFormData);
        if (onUpdate) {
          onUpdate(updatedFormData);
        }

        setSaveMessage({ type: "success", text: "Skills updated successfully!" });
        setTimeout(() => {
          setSaveMessage({ type: "", text: "" });
        }, 3000);
      } else {
        setSaveMessage({
          type: "error",
          text: response.error || "Failed to update skills. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error updating skills:", error);
      setSaveMessage({
        type: "error",
        text: error.error || "An error occurred while updating your skills.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to initial form data
    if (initialFormData) {
      const profileData = {
        years_of_experience: initialFormData.years_of_experience || 
                           initialFormData.experience_years || 
                           user?.years_of_experience || 
                           user?.experience_years || 
                           "",
        industry_experience: initialFormData.industry_experience || 
                           initialFormData.industry || 
                           user?.industry || 
                           "",
        functional_experience: initialFormData.functional_experience || 
                             user?.functional_experience || 
                             "",
        technology_stack: initialFormData.technology_stack || 
                         user?.technology_stack || 
                         "",
      };
      setFormData(profileData);
    }
    setSaveMessage({ type: "", text: "" });
  };

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1 text-left">
            <Typography as="h2" className="text-lg font-bold text-primary-900 text-left">
              Skills
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600 text-left">
              Update your skills here.
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="medium"
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
              className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
            >
              Save
            </Button>
          </div>
        </div>

        {saveMessage.text && (
          <div
            className={`p-4 rounded-lg border ${
              saveMessage.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <Typography as="p" className="text-sm font-medium">
                {saveMessage.text}
              </Typography>
            </div>
          </div>
        )}

        <div ref={formRef}>
          <Form
            fields={getFormFields()}
            initialValues={formData}
            onChange={(data) => {
              // Update formData in real-time as user types
              setFormData(data);
            }}
            onSubmit={(data) => {
              setFormData(data);
            }}
            layout="horizontal-label"
            loading={isSaving}
            showActions={false}
          />
        </div>
      </div>
    </Card>
  );
};

export default SkillsTab;
